import { useState, useRef, useEffect } from 'react'
import { getTags, Tag, importPocket } from '../api/notes'

interface ImportExportDialogProps {
  isOpen: boolean
  onClose: () => void
  mode: 'import' | 'export'
  onSuccess?: () => void
}

export default function ImportExportDialog({ isOpen, onClose, mode, onSuccess }: ImportExportDialogProps) {
  const [tags, setTags] = useState<Tag[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  // ── Pocket import state ────────────────────────────────────────────────
  const [importTab, setImportTab] = useState<'standard' | 'pocket'>('standard')
  const [pocketFile, setPocketFile] = useState<File | null>(null)
  const pocketInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      loadTags()
      setProgress(0)
      setMessage('')
      setError('')
      setPocketFile(null)
      setImportTab('standard')
    }
  }, [isOpen])

  const loadTags = async () => {
    try {
      const data = await getTags()
      setTags(data || [])
    } catch (err) {
      console.error('Failed to load tags:', err)
      setTags([])
    }
  }

  const handleTagToggle = (tagName: string) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    )
  }

  const handleExport = async () => {
    setLoading(true)
    setError('')
    setMessage('')
    setProgress(0)

    try {
      // Build query params for selected tags
      const params = new URLSearchParams()
      if (selectedTags.length > 0) {
        const tagIds = tags.filter(t => selectedTags.includes(t.name)).map(t => t.id)
        tagIds.forEach(id => params.append('tag_ids', id))
      }

      setProgress(10)
      setMessage('Preparing export...')

      // Get auth token from localStorage
      const token = localStorage.getItem('access_token')
      const headers: HeadersInit = {
        'Accept': 'application/zip'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      setProgress(30)
      setMessage('Requesting notes from server...')

      const response = await fetch(`/api/v1/notes/export?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Export failed:', errorText)
        setError(`Failed to export notes: ${response.statusText}`)
        return
      }

      setProgress(60)
      setMessage('Downloading ZIP file...')

      // Get the blob and download
      const blob = await response.blob()

      setProgress(80)

      // Check if blob is valid and has content
      if (blob.size === 0) {
        setError('Export resulted in empty file. You may not have any notes to export.')
        return
      }

      setProgress(90)
      setMessage('Creating download...')

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `notes_export_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.zip`
      document.body.appendChild(a)
      a.click()

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 100)

      setProgress(100)
      setMessage('Export completed successfully!')

      // Auto-close after success
      setTimeout(() => {
        onSuccess?.()
      }, 1500)
    } catch (err) {
      console.error('Export error:', err)
      setError(`Failed to export notes: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setLoading(true)
    setError('')
    setMessage('')
    setProgress(0)

    try {
      setProgress(10)
      setMessage('Preparing files...')

      const formData = new FormData()

      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i])
      }

      if (selectedTags.length > 0) {
        formData.append('tags', JSON.stringify(selectedTags))
      }

      setProgress(30)
      setMessage('Uploading files...')

      // Get auth token from localStorage
      const token = localStorage.getItem('access_token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/v1/notes/import', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      })

      setProgress(70)
      setMessage('Processing notes...')

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Import failed:', errorText)
        setError(`Failed to import notes: ${response.statusText}`)
        return
      }

      const result = await response.json()

      setProgress(100)

      if (result.failed && result.failed.length > 0) {
        setMessage(`Imported ${result.count || 0} notes. ${result.failed.length} failed.`)
        setError(`Some files failed to import: ${result.failed.slice(0, 3).join(', ')}${result.failed.length > 3 ? '...' : ''}`)
      } else {
        setMessage(`Successfully imported ${result.count || 0} notes!`)
      }

      // Auto-close after success if no failures
      if (!result.failed || result.failed.length === 0) {
        setTimeout(() => {
          onSuccess?.()
        }, 1500)
      }
    } catch (err) {
      console.error('Import error:', err)
      setError(`Failed to import notes: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handlePocketImport = async () => {
    if (!pocketFile) return
    setLoading(true)
    setError('')
    setMessage('')
    setProgress(0)

    try {
      const result = await importPocket(pocketFile, selectedTags, (pct) => setProgress(pct))
      setProgress(100)

      if (result.failed && result.failed.length > 0) {
        setMessage(`Imported ${result.count} notes. ${result.failed.length} failed.`)
        setError(
          `Some items failed: ${result.failed.slice(0, 3).join(', ')}${result.failed.length > 3 ? '…' : ''}`,
        )
      } else {
        setMessage(`Successfully imported ${result.count} notes from Pocket!`)
        setTimeout(() => { onSuccess?.() }, 1500)
      }
    } catch (err) {
      setError(`Pocket import failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
      if (pocketInputRef.current) pocketInputRef.current.value = ''
      setPocketFile(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mode === 'import' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              )}
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'import' ? 'Import Notes' : 'Export Notes'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Import mode tabs */}
        {mode === 'import' && (
          <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-5">
            <button
              onClick={() => { setImportTab('standard'); setError(''); setMessage(''); setProgress(0) }}
              data-testid="import-tab-standard"
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                importTab === 'standard'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Standard Import
            </button>
            <button
              onClick={() => { setImportTab('pocket'); setError(''); setMessage(''); setProgress(0) }}
              data-testid="import-tab-pocket"
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                importTab === 'pocket'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Pocket Import
            </button>
          </div>
        )}

        {/* Tag Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {mode === 'import' ? 'Add tags to imported notes (optional)' : 'Filter by tags (optional)'}
          </label>
          <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
            {tags.length === 0 ? (
              <p className="text-gray-500 text-sm">No tags available</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <label
                    key={tag.id}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                      selectedTags.includes(tag.name)
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.name)}
                      onChange={() => handleTagToggle(tag.name)}
                      className="sr-only"
                    />
                    {tag.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        {loading && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Processing...</span>
              <span className="text-sm text-gray-600">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
            {message}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Standard Import Section */}
        {mode === 'import' && importTab === 'standard' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select ZIP file with HTML notes
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleImport}
                disabled={loading}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-secondary disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                Upload a ZIP file containing HTML files. Each HTML file will become a note.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or select individual files
              </label>
              <input
                type="file"
                accept=".html,.htm,.md,.txt"
                multiple
                onChange={handleImport}
                disabled={loading}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                Supports HTML, Markdown (.md), and text (.txt) files.
              </p>
            </div>
          </div>
        )}

        {/* Pocket Import Section */}
        {mode === 'import' && importTab === 'pocket' && (
          <div className="space-y-4" data-testid="pocket-import-section">
            {/* What is Pocket info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Import from Pocket</p>
              <p className="text-xs text-blue-700">
                Export your Pocket data from{' '}
                <a
                  href="https://getpocket.com/export"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  getpocket.com/export
                </a>
                {' '}and upload the ZIP file here. Bookmarks and highlights will be imported as notes.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Pocket export ZIP
              </label>
              <input
                ref={pocketInputRef}
                type="file"
                accept=".zip"
                data-testid="pocket-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null
                  setPocketFile(f)
                  setError('')
                  setMessage('')
                }}
                disabled={loading}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-secondary disabled:opacity-50"
              />
              {pocketFile && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: <span className="font-medium">{pocketFile.name}</span>{' '}
                  ({(pocketFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                CSV bookmark data and JSON highlight annotations are parsed server-side.
              </p>
            </div>

            <button
              onClick={handlePocketImport}
              disabled={loading || !pocketFile}
              data-testid="pocket-import-btn"
              className="w-full py-3 px-4 bg-primary text-white font-medium rounded-lg hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Importing…' : 'Import from Pocket'}
            </button>
          </div>
        )}

        {/* Export Section */}
        {mode === 'export' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {selectedTags.length === 0
                ? 'All notes will be exported as a ZIP file containing HTML files.'
                : `Notes with selected tags will be exported.`}
            </p>
            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full py-3 px-4 bg-primary text-white font-medium rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
            >
              {loading ? 'Exporting...' : 'Export Notes'}
            </button>
          </div>
        )}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
